/**
 * TypographySetup.js
 *
 * Compound design option for small text elements (buttons, labels, etc.):
 * font-size, font-weight, line-height, letter-spacing, text-transform,
 * color and color-on-hover — all gated behind one shared
 * `{name}Option` default/custom toggle, following the same pattern as
 * CssBox (bundled multi-value, one Linked/Option flag) and ColorSetup
 * (default/custom radio + ColorSelector).
 *
 * Storage convention (flat fields, so the existing style:{selector,property}
 * renderer needs no changes):
 *   {name}FontSize
 *   {name}FontWeight
 *   {name}LineHeight
 *   {name}LetterSpacing
 *   {name}TextTransform
 *   {name}Color
 *   {name}ColorHover
 *   {name}Option        <- single "default" | "custom" toggle for the group
 */

import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import ColorSelector from "./ColorSelector";
import CssBoxInput from "./CssBoxInput";
import { CSSUnitSelector } from "./CSSUnitSelector";
import DesignTooltip from "./DesignTooltip";
import { BuilderInputSetupPropTypes } from "./BuilderOptionPropTypes";
import { CSSUnits } from "../../../../../builder/consts";
import WidgetSettingsContext from "../../../../../contexts/WidgetSettingsContext";

const DEFAULT_FONT_WEIGHT_OPTIONS = [
  { label: "Regular", value: "400" },
  { label: "Medium", value: "500" },
  { label: "Semibold", value: "600" },
  { label: "Bold", value: "700" },
];

const DEFAULT_TEXT_TRANSFORM_OPTIONS = [
  { label: "None", value: "none" },
  { label: "UPPERCASE", value: "uppercase" },
  { label: "lowercase", value: "lowercase" },
  { label: "Capitalize", value: "capitalize" },
];

// fontSize and letterSpacing always carry an explicit CSS unit, so they use
// the same numeric+unit input as CssDimension. lineHeight stays plain text —
// unitless numbers (e.g. "1.4") are the standard convention there, and
// forcing a unit picker onto it would be a regression, not an improvement.
const DIMENSION_KEYS = ["fontSize", "letterSpacing"];

/**
 * Splits a raw CSS value like "14px" into its numeric part and unit,
 * falling back to the first known unit if none is present/recognized —
 * mirrors the parsing CssDimension.js already does for the same reason.
 */
const parseDimension = (raw, fallbackUnit = CSSUnits[0]) => {
  const match = String(raw ?? "").match(/(-?\d*\.?\d+)([a-zA-Z%]*)/);

  if (!match) {
    return { value: "", unit: fallbackUnit };
  }

  const [, value, unit] = match;
  return { value, unit: CSSUnits.includes(unit) ? unit : fallbackUnit };
};

const subField = (name, suffix) => `${name}${suffix}`;

const TypographySetup = (props) => {
  const {
    title,
    name,
    defaultValue = {},
    control,
    settingsName,
    updateDesignProp,
  } = props;

  const {
    showFontSize = true,
    showFontWeight = true,
    showLineHeight = false,
    showLetterSpacing = false,
    showTextTransform = false,
    showColor = true,
    showColorHover = false,
    fontWeightOptions = DEFAULT_FONT_WEIGHT_OPTIONS,
    textTransformOptions = DEFAULT_TEXT_TRANSFORM_OPTIONS,
  } = control.options || {};

  const nameOption = `${name}Option`;
  const inputName = `${settingsName}-typography-setup-${name}`;

  const widgetSettingsContext = useContext(WidgetSettingsContext);
  const optionValue = widgetSettingsContext[nameOption] || "default";
  const isCustom = optionValue === "custom";

  const fields = {
    fontSize: subField(name, "FontSize"),
    fontWeight: subField(name, "FontWeight"),
    lineHeight: subField(name, "LineHeight"),
    letterSpacing: subField(name, "LetterSpacing"),
    textTransform: subField(name, "TextTransform"),
    color: subField(name, "Color"),
    colorHover: subField(name, "ColorHover"),
  };

  /**
   * Builds the set of values actually shown on screen, from whichever
   * of (context value, schema default) is available per field.
   */
  const buildDisplayValues = useCallback(
    () =>
      Object.entries(fields).reduce((acc, [key, fieldName]) => {
        acc[key] = widgetSettingsContext[fieldName] ?? defaultValue[key] ?? "";
        return acc;
      }, {}),
    // fields/defaultValue are derived from name/control/props, stable per option
    [widgetSettingsContext, name]
  );

  /**
   * Local state is the source of truth for what's rendered — same reason
   * CssBox/CssDimension keep their own useState instead of reading
   * WidgetSettingsContext directly on every keystroke: the store round-trip
   * (dispatch -> reducer -> context re-render) isn't guaranteed to land
   * before the next render, so a text input reading straight from context
   * can visibly lag or "snap back" to the old value. Typing updates local
   * state immediately; the store update happens alongside it as a
   * fire-and-forget side effect, not as the render's data source.
   */
  const [displayValues, setDisplayValues] = useState(buildDisplayValues);

  /**
   * Unit portion of fontSize/letterSpacing, tracked separately from the
   * numeric portion so switching PX -> EM doesn't require re-typing the
   * number, same as CssDimension.
   */
  const buildUnits = useCallback(
    () =>
      DIMENSION_KEYS.reduce((acc, key) => {
        acc[key] = parseDimension(
          widgetSettingsContext[fields[key]] ?? defaultValue[key]
        ).unit;
        return acc;
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetSettingsContext, name]
  );

  const [units, setUnits] = useState(buildUnits);

  // Deliberately NOT seeded from optionValue: even a calendar that already
  // has custom typography saved should load collapsed, so an already-set
  // custom config doesn't dump every field into view unprompted. It only
  // auto-opens as an immediate reaction to the user flipping the radio to
  // Custom themselves (see onOptionChange below), and can be toggled
  // independently of that radio afterwards.
  const [expanded, setExpanded] = useState(false);

  // Re-seed local display state whenever the group is (re)switched to
  // custom, or when a different underlying widget/option is loaded.
  useEffect(() => {
    setDisplayValues(buildDisplayValues());
    setUnits(buildUnits());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionValue, name]);

  /**
   * Flip the whole group between default/custom. Switching to custom
   * seeds every sub-field from defaultValue so nothing renders blank.
   */
  const onOptionChange = useCallback(
    (kind) => () => {
      const updates = [{ field: nameOption, value: kind }];

      if (kind === "custom") {
        Object.entries(fields).forEach(([key, fieldName]) => {
          updates.push({
            field: fieldName,
            value: widgetSettingsContext[fieldName] ?? defaultValue[key] ?? "",
          });
        });
      }

      // Custom -> immediately reveal the fields, since the user just asked
      // to customize something. Default -> nothing to show, so collapse.
      setExpanded(kind === "custom");
      updateDesignProp(updates);
    },
    [updateDesignProp, widgetSettingsContext, defaultValue]
  );

  const onTextFieldChange = useCallback(
    (key, fieldName) => (e) => {
      const { value } = e.target;
      setDisplayValues((prev) => ({ ...prev, [key]: value }));
      updateDesignProp([{ field: fieldName, value }]);
    },
    [updateDesignProp]
  );

  const onDropdownChange = useCallback(
    (key, fieldName) => (e) => {
      const { value } = e.target;
      setDisplayValues((prev) => ({ ...prev, [key]: value }));
      updateDesignProp([{ field: fieldName, value }]);
    },
    [updateDesignProp]
  );

  const onColorChange = useCallback(
    (key, fieldName) => (color) => {
      const { r, g, b, a } = color.rgb;
      const alpha = Math.round(a * 255);
      const hex =
        "#" +
        r.toString(16).padStart(2, "0") +
        g.toString(16).padStart(2, "0") +
        b.toString(16).padStart(2, "0") +
        alpha.toString(16).padStart(2, "0");
      setDisplayValues((prev) => ({ ...prev, [key]: hex }));
      updateDesignProp([{ field: fieldName, value: hex }]);
    },
    [updateDesignProp]
  );

  const onDimensionValueChange = useCallback(
    (key, fieldName) => (rawValue) => {
      const combined = `${rawValue}${units[key]}`;
      setDisplayValues((prev) => ({ ...prev, [key]: combined }));
      updateDesignProp([{ field: fieldName, value: combined }]);
    },
    [updateDesignProp, units]
  );

  const onDimensionUnitChange = useCallback(
    (key, fieldName) => (unit) => {
      const { value } = parseDimension(displayValues[key], units[key]);
      const combined = `${value}${unit}`;
      setUnits((prev) => ({ ...prev, [key]: unit }));
      setDisplayValues((prev) => ({ ...prev, [key]: combined }));
      updateDesignProp([{ field: fieldName, value: combined }]);
    },
    [updateDesignProp, displayValues, units]
  );

  if (!props.show) {
    return null;
  }

  /**
   * Places two rows side by side when both are present (both `show*`
   * flags true), falls back to a single full-width row when only one is,
   * and renders nothing when neither is — so toggling a field off never
   * leaves a lopsided half-empty row.
   */
  const renderPair = (a, b) => {
    if (a && b) {
      return (
        <div className="typography-row-pair">
          {a}
          {b}
        </div>
      );
    }
    return a || b || null;
  };

  const dimensionRow = (label, key, fieldName, placeholder) => {
    const { value } = parseDimension(displayValues[key], units[key]);

    return (
      <div className="typography-row" key={fieldName}>
        <div className="d-flex align-items-center justify-content-between">
          <span className="typography-row-label">{label}</span>
          <CSSUnitSelector
            value={units[key]}
            setValue={onDimensionUnitChange(key, fieldName)}
            disabled={!isCustom}
          />
        </div>
        <CssBoxInput
          value={value}
          setValue={onDimensionValueChange(key, fieldName)}
          placeholder={placeholder}
          disabled={!isCustom}
        />
      </div>
    );
  };

  const textRow = (label, key, fieldName, placeholder) => (
    <div className="typography-row" key={fieldName}>
      <span className="typography-row-label">{label}</span>
      <input
        disabled={!isCustom}
        value={displayValues[key] ?? ""}
        onChange={onTextFieldChange(key, fieldName)}
        type="text"
        className="form-control"
        placeholder={placeholder}
      />
    </div>
  );

  const dropdownRow = (label, key, fieldName, dropdownOptions) => (
    <div className="typography-row" key={fieldName}>
      <span className="typography-row-label">{label}</span>
      <select
        disabled={!isCustom}
        className="form-control"
        value={displayValues[key] ?? ""}
        onChange={onDropdownChange(key, fieldName)}
      >
        {dropdownOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  const colorRow = (label, key, fieldName) => (
    <div className="typography-row typography-row-color" key={fieldName}>
      <span className="typography-row-label">{label}</span>
      <ColorSelector
        id={`${inputName}-${fieldName}`}
        disabled={!isCustom}
        color={displayValues[key] ?? ""}
        onColorChagnge={onColorChange(key, fieldName)}
      />
    </div>
  );

  return (
    <div className={"design-element"}>
      <label className="option-label">
        <span className={"option-title"}>{title}</span>
        <DesignTooltip help={props.tooltip} />
      </label>

      <div className="form-group typography-setup-row">
        <div className="radio">
          <label>
            <input
              type="radio"
              name={inputName}
              checked={optionValue === "default"}
              onChange={onOptionChange("default")}
            />{" "}
            Default
          </label>
        </div>
        <div className="radio">
          <label>
            <input
              type="radio"
              name={inputName}
              checked={isCustom}
              onChange={onOptionChange("custom")}
            />{" "}
            Custom
          </label>
        </div>
      </div>

      {isCustom && (
        <button
          type="button"
          className="surface-setup-expand-toggle"
          onClick={() => setExpanded((prev) => !prev)}
        >
          <i className="material-icons" aria-hidden="true">
            {expanded ? "expand_less" : "expand_more"}
          </i>{" "}
          {expanded ? "Hide custom settings" : "Show custom settings"}
        </button>
      )}

      {isCustom && expanded && (
        <div className="typography-setup-fields">
          {showFontSize &&
            dimensionRow("Font size", "fontSize", fields.fontSize, "14")}
          {showFontWeight &&
            dropdownRow(
              "Font weight",
              "fontWeight",
              fields.fontWeight,
              fontWeightOptions
            )}
          {showLineHeight &&
            textRow("Line height", "lineHeight", fields.lineHeight, "e.g. 1.4")}
          {showLetterSpacing &&
            dimensionRow(
              "Letter spacing",
              "letterSpacing",
              fields.letterSpacing,
              "0"
            )}
          {showTextTransform &&
            dropdownRow(
              "Text transform",
              "textTransform",
              fields.textTransform,
              textTransformOptions
            )}
          {renderPair(
            showColor && colorRow("Color", "color", fields.color),
            showColorHover &&
              colorRow("Color on hover", "colorHover", fields.colorHover)
          )}
        </div>
      )}
    </div>
  );
};

TypographySetup.propTypes = {
  ...BuilderInputSetupPropTypes,

  defaultValue: PropTypes.shape({
    fontSize: PropTypes.string,
    fontWeight: PropTypes.string,
    lineHeight: PropTypes.string,
    letterSpacing: PropTypes.string,
    textTransform: PropTypes.string,
    color: PropTypes.string,
    colorHover: PropTypes.string,
  }),

  control: PropTypes.shape({
    kind: PropTypes.oneOf(["typography"]).isRequired,
    options: PropTypes.shape({
      showFontSize: PropTypes.bool,
      showFontWeight: PropTypes.bool,
      showLineHeight: PropTypes.bool,
      showLetterSpacing: PropTypes.bool,
      showTextTransform: PropTypes.bool,
      showColor: PropTypes.bool,
      showColorHover: PropTypes.bool,
      fontWeightOptions: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
      ),
      textTransformOptions: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
      ),
    }),
  }).isRequired,
};

TypographySetup.defaultProps = {
  control: {
    kind: "typography",
    options: {
      showFontSize: true,
      showFontWeight: true,
      showColor: true,
    },
  },
};

export default TypographySetup;
